import React, { useMemo, useState } from 'react';

import { gear, plus } from '@/assets/icons';
import { EAccountDisplayState } from '@/components/AccountPanel/AccountPanel';
import {
	BottomCardAction,
	BottomCardForm,
} from '@/components/shared/BottomCard/BottomCard';
import { AsaphButton } from '@/components/shared/Button';
import { DatePicker } from '@/components/shared/DatePicker';
import { FormInput } from '@/components/shared/Inputs';
import { Blurb } from '@/components/shared/Inputs/Blurb';
import { Label } from '@/components/shared/Inputs/Label';
import { Select } from '@/components/shared/Inputs/Select';
import {
	SettingsList,
	SettingsListItems,
} from '@/components/shared/SettingCard';
import { TimePicker } from '@/components/shared/TimePicker';
import { DAYS_OF_WEEK } from '@/config/constants';
import { Permissions } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { hr24to12 } from '@/helpers/timeFormatter';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useServices } from '@/providers/ServicesProvider';
import constants from '@/theme/exportedVariables.module.scss';
import { Capacitor } from '@capacitor/core';
import {
	ListItemIcon,
	ListItemText,
	MenuItem,
	SvgIcon,
	useMediaQuery,
} from '@material-ui/core';
import { Day, getDay, nextDay } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';

import { IPropsCreateSetlistDisplay } from '../../CreateSetlistPanel';
import styles from './SecondStep.module.scss';

const isCapacitor = Capacitor.isNativePlatform();
// const isCapacitor = true;

export interface SecondStepProps {
	startTime: string;
	startDate: string;
	setStartTime: React.Dispatch<string>;
	setStartDate: React.Dispatch<string>;
	setSelectedServiceId: React.Dispatch<string | undefined | null>;
	onSave: () => void;
	selectedServiceId?: string | null;
	setAccountOpen: IPropsCreateSetlistDisplay['setAccountOpen'];
}

export const SecondStep: React.FC<SecondStepProps> = ({
	startTime,
	startDate,
	setStartTime,
	setStartDate,
	setSelectedServiceId,
	onSave,
	selectedServiceId,
	setAccountOpen,
}) => {
	const { services } = useServices();

	const [touched, setTouched] = useState(false);
	const IANATimeZone = useMemo(
		() => Intl.DateTimeFormat().resolvedOptions().timeZone,
		[],
	);
	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet}`);
	const { hasPermission } = usePermissions();

	const selectedService = services.find((s) => s.id === selectedServiceId);

	const offsetInHours =
		getTimezoneOffset(IANATimeZone, new Date()) / 1000 / 60 / 60;
	const UTCOffset = `GMT${offsetInHours >= 0 ? '+' : ''}${offsetInHours}`;

	// If there is a slectedService then check if the week day of the selected start date matches
	const isWeekDayValid =
		selectedService && touched
			? !!startDate && selectedService.day === getDay(new Date(startDate))
			: true;

	const serviceSettingItems: SettingsListItems = services.map((s) => ({
		type: 'setting',
		title: s.name,
		onClick: () => {
			setSelectedServiceId(s.id);
			setStartTime(s.startAt);
			setStartDate(nextDay(new Date(), s.day as Day).toISOString());
		},
		disabled: !hasPermission(Permissions.ServiceUpdate),
		trailingText: `${DAYS_OF_WEEK[s.day]}, ${hr24to12(s.startAt)}`,
	}));

	return (
		<>
			<BottomCardForm>
				<Label withOffset>Services</Label>
				<Select
					value={
						selectedServiceId === null
							? 'Custom service'
							: selectedService?.name
					}
					placeholder="Regular or custom service"
					withBorder
					items={[]}
					dropdownClassName={styles['select']}
					listClassName={styles['list']}
					customContent={(close) => (
						<>
							<SettingsList
								items={serviceSettingItems}
								noGrouping
								noChevron
							/>

							<div className={styles['divider']} />
							<div className={styles['footer']}>
								<MenuItem
									onClick={() => {
										setAccountOpen({
											open: true,
											defaultDisplayState:
												EAccountDisplayState.SERVICES,
										});
									}}
									classes={{
										root: classNames([
											styles['list-item'],
											styles['add'],
											isTablet && styles['hover'],
										]),
									}}
								>
									<ListItemIcon
										classes={{
											root: styles['icon'],
										}}
									>
										<SvgIcon component={plus} />
									</ListItemIcon>
									<ListItemText>
										Add a regular service
									</ListItemText>
								</MenuItem>
								<MenuItem
									onClick={() => {
										setSelectedServiceId(null);
										close();
									}}
									classes={{
										root: classNames([
											styles['list-item'],
											isTablet && styles['hover'],
										]),
									}}
								>
									<ListItemIcon
										classes={{
											root: styles['icon'],
										}}
									>
										<SvgIcon component={gear} />
									</ListItemIcon>
									<ListItemText>
										Use a custom service
									</ListItemText>
								</MenuItem>
							</div>
						</>
					)}
				/>
				<div className="vsb-20" />
				<Label withOffset>Service date</Label>
				<DatePicker
					value={startDate}
					onChange={setStartDate}
					filterDate={(date) => {
						if (!selectedService) {
							return true;
						}
						const day = getDay(date);
						return day === selectedService.day;
					}}
					error={
						!isWeekDayValid || (touched && !startDate)
							? 'Please enter a valid date'
							: ''
					}
				/>
				<div className="vsb-20" />

				<Label withOffset>Service start time</Label>
				{isCapacitor ? (
					<FormInput
						type="time"
						placeholder="Select time"
						name="time"
						id="time"
						autoComplete="off"
						value={startTime}
						onChange={(e) => {
							setStartTime(e.currentTarget.value);
						}}
						error={touched && !startTime}
					/>
				) : (
					<TimePicker
						value={startTime}
						onChange={setStartTime}
						error={
							touched && !startTime
								? 'Please enter the service start time'
								: ''
						}
					/>
				)}
				<Blurb>
					Please note that your local timezone will be used when
					setting the time. This is currently {IANATimeZone} (
					{UTCOffset}
					).
				</Blurb>
			</BottomCardForm>
			<BottomCardAction>
				<AsaphButton
					onClick={() => {
						setTouched(true);
						const isWeekDayValid = selectedService
							? !!startDate &&
							  selectedService.day ===
									getDay(new Date(startDate))
							: true;
						if (startTime && startDate && isWeekDayValid) {
							onSave();
						}
					}}
					color="blue"
					label="Save"
					fullWidth
				/>
			</BottomCardAction>
		</>
	);
};
